import React, { useRef } from 'react'
import { Collapse, IconButton, Paper } from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import parse from 'html-react-parser'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import StatusIcon, {
  STATUS_TYPE_BASE,
  STATUS_TYPE_IN_PROGRESS,
  STATUS_TYPE_COMPLETE,
} from 'routes/App/Apps/DailyLogbookEvents/Questionnaire/StatusIcon'
import { getFormattedDate, getDateFormat } from 'helpers/dates'
import { ReactComponent as AddIcon } from 'assets/img/add-button.svg'
import { ReactComponent as RemoveIcon } from 'assets/img/remove-button.svg'
import { ReactComponent as AddWithDataIcon } from 'assets/img/add-with-data-button.svg'
import withPromise from 'helpers/withPromise'
import { embedableYoutubeVideo } from 'helpers/youtubeVideo'
import {
  ACTION_DUPLICATE,
  ACTION_DUPLICATE_WITH_DATA,
  DUPLICATE_INSERT_PARAMETER_AFTER,
} from 'constants/index'
import InputGroupInstructions from 'routes/App/Apps/MyJobs/JobTask/InputGroupInstructions'
import JobField from '../JobFields'
import { canRenderConditionalInputOrGroup } from '../Common/utils'
import JobTaskUnits from './JobTaskUnits'
import JobTaskPolicies from './JobTaskPolicies'
import messages from '../translate'
import { CONDITIONAL_ITEM_TYPE } from '../Common/Common.types'

function CollapsibleUnits({
  activeUnits,
  activeUnitId,
  findInputData,
  fieldState,
  onJobTaskAutoSave,
  onJobTaskValueRange,
  onJobTaskFocusedInput,
  availableInputs,
  input_items,
  outputs,
  needs_locus,
  isTaskCompleted,
  needs_locus_group,
  locusGroup,
  locus,
  getSuitableTask,
  setCurrentTaskUnits,
  evt_tasks,
  locusGroupList,
  type,
  handleUnitsChange,
  checkData,
  policy_indicators,
  data: { name, locuses, id: currentId },
  intl: { formatMessage },
  id: currentTaskId,
  currentTask,
  findChildren,
  resetInvalidDates,
  activeJob: { id: activeJobId },
  deleteDuplicatedInputGroup,
  fetchJobTaskList,
  handleDependentLookupInputs,
  savingEvtJobEntry,
  handleDuplicateInputGroup,
  clearCurrentTask,
  evtInputs,
  onJobTaskListRefresh,
  licensee: { usaDateFormat },
  isDebouncing,
  setIsDebouncing,
  getIsDisabledLookupInput,
  getDependentLookupInputHint,
  errors,
  featureFlags,
}) {
  const collapsibleUnitRef = useRef()
  const EVALUATION_SOURCE_KEY = '_evaluation_aggregation_source'
  const EVALUATION_SCOPE_KEY = '_evaluation_aggregation_scope'
  const EVALUATION_ROUTE_KEY = '_evaluation_aggregation_route'
  let inputGroupIndex = 1
  const checkResult = result => result || result === false || result === 0
  const dateFormat = getDateFormat(usaDateFormat)

  const getScopedContext = group => group.duplicate_context || {}

  const isEvaluationAggregationParentTask = input_items.some(
    item =>
      item.input_item_type === 'input_group' &&
      (item?.parameters?.[EVALUATION_SOURCE_KEY] ||
        item?.parameters?.[EVALUATION_SCOPE_KEY] ||
        item?.parameters?.[EVALUATION_ROUTE_KEY] ||
        (Array.isArray(item?.preview_input_output) &&
          item.preview_input_output.length > 0)),
  )

  const isGroupForActiveUnit = group => {
    const scopedContext = getScopedContext(group)
    if (isEmpty(scopedContext)) {
      return true
    }

    const noNeedLocusGroupCondition =
      needs_locus && !needs_locus_group
        ? scopedContext.locus === activeUnitId
        : scopedContext.locus_group === activeUnitId

    if (needs_locus && needs_locus_group) {
      return (
        scopedContext.locus === locus &&
        scopedContext.locus_group === activeUnitId
      )
    }

    return noNeedLocusGroupCondition
  }

  const isInputValueExist = (inputList, allExist = false) => {
    if (Array.isArray(inputList)) {
      if (!allExist) {
        return inputList.some(({ result }) => checkResult(result))
      }
      if (needs_locus_group && needs_locus) {
        return (
          inputList.every(({ result }) => checkResult(result)) &&
          locuses.every(locusData =>
            evt_tasks.some(
              ({ evt_inputs, locus: locusItem }) =>
                locusItem === locusData.id &&
                evt_inputs.length === inputList.length &&
                evt_inputs.every(({ result }) => checkResult(result)),
            ),
          )
        )
      }

      return inputList.every(({ result }) => checkResult(result))
    }
  }

  const handleClick = elementId => {
    if (!savingEvtJobEntry && !isDebouncing) {
      handleUnitsChange(elementId)
      setCurrentTaskUnits(type, elementId, true)
    }
  }

  const getFieldStatus = (field, id) => {
    const checkedField = field === 'locusGroup' ? 'locus_group' : 'locus'
    const checkEvtTasks = evt_tasks && evt_tasks.length > 0
    const inputsLength = input_items.filter(
      ({ input_item_type }) => input_item_type === 'input',
    ).length
    const inputsInGroupsLength = input_items
      .filter(({ input_item_type, duplicate_context }) =>
        input_item_type === 'input_group' && !isEmpty(duplicate_context)
          ? duplicate_context[checkedField] === id
          : true,
      )
      .reduce(
        (prevVal, { inputs }) => prevVal + (inputs ? inputs.length : 0),
        0,
      )
    const allFilled =
      checkEvtTasks &&
      evt_tasks.some(
        item =>
          item[checkedField] === id &&
          inputsLength + inputsInGroupsLength === item.evt_inputs.length &&
          isInputValueExist(item.evt_inputs, true),
      )
    const someFilled =
      checkEvtTasks &&
      evt_tasks.some(
        item => item[checkedField] === id && isInputValueExist(item.evt_inputs),
      )

    if (allFilled) {
      return STATUS_TYPE_COMPLETE
    }

    if (someFilled) {
      return STATUS_TYPE_IN_PROGRESS
    }

    return STATUS_TYPE_BASE
  }

  const getStatusIcon = (unitType, currentUnitId, unitName) => {
    const iconType = getFieldStatus(unitType, currentUnitId)

    return (
      <>
        <StatusIcon type={iconType} />
        {unitName}
      </>
    )
  }

  const isOpen =
    activeUnits[activeUnitId] === 'open' && activeUnitId === currentId

  const renderOutputs = () => (
    <div className="job-outputs-list">
      {outputs.map(output => {
        const { values, id, title } = output
        const { value, date_updated } =
          (values.length &&
            values.find(({ locus: currentLocus, locus_group }) => {
              if (currentLocus && locus_group) {
                return currentLocus === locus && locus_group === locusGroup
              }
              if (currentLocus && !locus_group) {
                return currentLocus === currentId
              }

              return locus_group === currentId
            })) ||
          {}

        return (
          <div className="output" key={id}>
            <div>
              {`${title}: `}
              <b>{value || value === 0 ? parse(value) : 'N/A'}</b>
            </div>
            {!!date_updated && (
              <div className="date-updated">
                {/* eslint-disable-next-line max-len */}
                {`${formatMessage(messages.lastUpdated)}: ${getFormattedDate(
                  date_updated,
                  dateFormat,
                )}`}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  const renderInput = (input, group) => {
    const {
      id,
      parameters: {
        dependency: { input: inputID } = {},
        lookup_table_source,
        display,
      },
    } = input
    const dependency = { value: '', title: '' }
    const isCheckbox = display === 'checkbox'
    const isInputDisabled =
      fieldState || getIsDisabledLookupInput(lookup_table_source)
    const inputHint = getDependentLookupInputHint(lookup_table_source)
    const inputs = input_items.filter(
      ({ input_item_type }) => input_item_type === 'input',
    )
    const inputGroups = input_items.filter(
      ({ input_item_type }) => input_item_type === 'input_group',
    )
    let inputGroupsInputs = []

    if (inputGroups.length) {
      inputGroups.forEach(({ inputs }) => {
        inputGroupsInputs = [...inputGroupsInputs, ...inputs]
      })
    }

    const allInputs = [...inputGroupsInputs, ...inputs]

    if (inputID) {
      const { title, type } = allInputs.find(({ id }) => id === inputID) || {}
      dependency.title = title
      dependency.type = type

      if (currentTask.evt_inputs) {
        const { result } =
          currentTask.evt_inputs.find(({ input }) => inputID === input) || {}

        if (result) {
          dependency.value = result
        }
      }
    }

    const filledChildren = findChildren(allInputs, currentTask, input.id)

    return (
      <JobField
        key={id}
        index={id}
        className={cn('job-field-main-box', { isCheckbox, isInputDisabled })}
        inputInfo={input}
        disabled={isInputDisabled}
        isTaskCompleted={isTaskCompleted}
        isUnitBlockOpen={isOpen}
        activeJobId={activeJobId}
        inputData={findInputData(id)}
        dependency={{
          dependency,
          children: filledChildren,
          currentEvtTask: currentTask,
          currentTaskID: currentTaskId,
        }}
        onJobTaskAutoSave={onJobTaskAutoSave}
        onJobTaskValueRange={onJobTaskValueRange}
        onJobTaskFocusedInput={onJobTaskFocusedInput}
        resetInvalidDates={resetInvalidDates}
        clearCurrentTask={clearCurrentTask}
        handleDependentLookupInputs={handleDependentLookupInputs}
        inputs={
          group
            ? [...group.inputs, ...inputs]
            : [...inputGroupsInputs, ...inputs]
        }
        isDebouncing={isDebouncing}
        setIsDebouncing={setIsDebouncing}
        inputHint={inputHint}
        errors={errors}
      />
    )
  }

  const renderInputGroup = (group, duplicateMatches) => {
    const {
      title,
      inputs,
      duplicable,
      id,
      original_input_group_id,
      instructions,
      image,
      video,
      preview_input_output,
      parameters,
      availability,
    } = group
    const { id: evtTaskId } = currentTask
    const isInputGroupValid = inputs.length && isGroupForActiveUnit(group)

    const hasPreviewOutput = Array.isArray(preview_input_output)
      ? preview_input_output.length > 0
      : false
    const hideEvaluationAggregationDuplicateControls = Boolean(
      isEvaluationAggregationParentTask &&
        (parameters?.[EVALUATION_SOURCE_KEY] ||
          parameters?.[EVALUATION_ROUTE_KEY] ||
          hasPreviewOutput),
    )
    const showDuplicateControls =
      Boolean(duplicable) && !hideEvaluationAggregationDuplicateControls
    let payloadLocus
    let payloadLocusGroup

    if (!original_input_group_id) {
      inputGroupIndex = 1
    } else if (isInputGroupValid) {
      inputGroupIndex += 1
    }

    if (needs_locus && needs_locus_group) {
      payloadLocus = locus
      payloadLocusGroup = activeUnitId
    } else if (needs_locus && !needs_locus_group) {
      payloadLocus = activeUnitId
    } else {
      payloadLocusGroup = activeUnitId
    }

    if (
      canRenderConditionalInputOrGroup(
        evtInputs,
        parameters,
        availability,
        evtTaskId,
        id,
        CONDITIONAL_ITEM_TYPE.INPUT_GROUPS,
      ) &&
      featureFlags.CONDITIONAL_INPUTS
    ) {
      return null
    }

    const isSomeInputsAvailable = inputs?.some(
      input =>
        !canRenderConditionalInputOrGroup(
          evtInputs,
          input.parameters,
          input.availability,
          evtTaskId,
          input.id,
          CONDITIONAL_ITEM_TYPE.INPUTS,
        ),
    )

    return isInputGroupValid && isSomeInputsAvailable ? (
      <section className="input-group" key={id}>
        {!original_input_group_id && <div className="title">{title}</div>}
        <div className="input-container">
          {(instructions || embedableYoutubeVideo(video) || image) && (
            <InputGroupInstructions group={group} />
          )}
          {(!!duplicateMatches.length || !!original_input_group_id) && (
            <div className="input-group-index">{inputGroupIndex}</div>
          )}
          {hasPreviewOutput && (
            <div className="job-task-notification">
              <span className="job-task-notification-icon">i</span>
              {preview_input_output.map(
                ({ short_name, title: previewTitle, value }, index) => {
                  const labelSource = short_name || previewTitle || ''
                  const label = labelSource
                    .toString()
                    .replace(/_/g, ' ')
                    .replace(/^\w/, char => char.toUpperCase())
                  const valueText =
                    !!value || value === false || value === 0
                      ? String(value).replace(/^\n+/, '')
                      : null
                  // Actions always starts on a new line (even a single Farm/House entry).
                  const isMultiline =
                    short_name === 'actions' ||
                    (typeof valueText === 'string' &&
                      (valueText.includes('\n') ||
                        valueText.trimStart().startsWith('- ')))

                  return (
                    <div
                      key={`${
                        short_name || previewTitle || 'preview'
                      }-${index}`}
                      className="job-task-notification-title">
                      <strong>{`${label}:${isMultiline ? '' : ' '}`}</strong>
                      {valueText !== null && (
                        <span
                          className={
                            isMultiline
                              ? 'job-task-notification-value-multiline'
                              : undefined
                          }>
                          {valueText}
                        </span>
                      )}
                    </div>
                  )
                },
              )}
            </div>
          )}
          {!!original_input_group_id &&
            !hideEvaluationAggregationDuplicateControls && (
              <IconButton
                size="small"
                className="remove-duplicate-button"
                onClick={() => {
                  const previewInputs = inputs
                    .filter(({ preview }) => preview)
                    .map(({ id }) => id)
                  const previewsToRemove = evtInputs
                    .filter(({ input }) =>
                      previewInputs.find(id => id === input),
                    )
                    .map(({ id }) => id)

                  withPromise(deleteDuplicatedInputGroup, {
                    id,
                  }).then(response => {
                    if (!response.error) {
                      fetchJobTaskList({
                        activeJobId,
                        previewsToRemove,
                      })

                      if (previewsToRemove.length) {
                        onJobTaskListRefresh()
                      }
                    }
                  })
                }}>
                <RemoveIcon />
              </IconButton>
            )}
          {inputs.map(input => renderInput(input, group))}
        </div>
        {showDuplicateControls && (
          <div className="duplication-block">
            <IconButton
              size="small"
              className="duplicate-button"
              disabled={fieldState}
              onClick={() => {
                if (!fieldState) {
                  handleDuplicateInputGroup(
                    DUPLICATE_INSERT_PARAMETER_AFTER,
                    ACTION_DUPLICATE_WITH_DATA,
                    id,
                    payloadLocus,
                    payloadLocusGroup,
                  )
                }
              }}>
              <AddWithDataIcon />
            </IconButton>
            <IconButton
              size="small"
              className="duplicate-button"
              disabled={fieldState}
              onClick={() => {
                if (!fieldState) {
                  handleDuplicateInputGroup(
                    DUPLICATE_INSERT_PARAMETER_AFTER,
                    ACTION_DUPLICATE,
                    id,
                    payloadLocus,
                    payloadLocusGroup,
                  )
                }
              }}>
              <AddIcon />
            </IconButton>
          </div>
        )}
      </section>
    ) : null
  }

  const renderInputList = () =>
    input_items.map(item => {
      const { input_item_type, id, original_input_group_id } = item

      if (input_item_type === 'input') {
        return renderInput(item)
      }

      const duplicatedGroups = input_items.filter(
        group =>
          group.input_item_type === 'input_group' &&
          group.original_input_group_id &&
          isGroupForActiveUnit(group),
      )
      const duplicateMatches = duplicatedGroups.filter(
        group =>
          id === group.original_input_group_id ||
          id === group.id ||
          original_input_group_id === group.original_input_group_id,
      )

      return renderInputGroup(item, duplicateMatches)
    })

  const onCollapsibleUnitToggle = () => {
    if (collapsibleUnitRef) {
      collapsibleUnitRef.current.scrollIntoView()
    }
  }

  return (
    <Paper
      className={cn('job-collapsible-units', { isOpen })}
      ref={collapsibleUnitRef}>
      <div
        role="presentation"
        className="status-wrapper"
        onClick={() => handleClick(currentId)}>
        {getStatusIcon(type, currentId, name)}
      </div>
      <Collapse
        in={isOpen}
        onClick={e => e.stopPropagation()}
        onEntered={onCollapsibleUnitToggle}
        onExited={() => {
          if (activeUnitId === currentId) {
            onCollapsibleUnitToggle()
          }
        }}>
        <JobTaskPolicies
          locus={availableInputs.locus}
          needsLocus={needs_locus}
          needsLocusGroup={needs_locus_group}
          policyIndicators={policy_indicators}
        />
        {needs_locus && needs_locus_group && (
          <JobTaskUnits
            evt_tasks={evt_tasks ? [...evt_tasks] : []}
            locus={locus}
            locusGroup={locusGroup}
            locusList={locuses}
            locusGroupList={locusGroupList}
            needs_locus={needs_locus}
            needs_locus_group={false}
            input_items={input_items}
            getSuitableTask={getSuitableTask}
            setCurrentTaskUnits={setCurrentTaskUnits}
          />
        )}
        {availableInputs && isOpen && (
          <>
            {checkData(outputs) && renderOutputs()}
            {renderInputList()}
          </>
        )}
        <div
          role="presentation"
          className={cn('dropdown-icon', { isOpen })}
          onClick={() => handleClick(currentId)}>
          <ExpandMore />
        </div>
      </Collapse>
    </Paper>
  )
}

export default connect(state => ({
  featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
}))(injectIntl(CollapsibleUnits))
